/**
 * iter168 — Capacitor push-notification bootstrap.
 *
 * Runs once at app startup (App.js calls `initPushNotifications()`).
 *
 *   • Web: returns immediately — push only works in the native shell.
 *   • Native: requests permission, registers with APNs/FCM, POSTs the
 *     token to /api/push/register, and wires up foreground +
 *     background tap handlers.
 *
 * Token rotation: Firebase fires `registration` again whenever the
 * token rotates (app reinstall, clear data, OS major upgrade).  We
 * re-POST every time, so the backend `device_tokens` collection
 * always has the latest.
 *
 * Deep-link routing: when a user taps a backgrounded notification,
 * Capacitor fires `pushNotificationActionPerformed` with the data
 * payload our backend attached.  We look for `deep_link` and
 * navigate there.  The most common case is `kind=dispatch_assigned`
 * with `deep_link=/field?wo_id=...`.
 */
import { toast } from "sonner";

const REGISTER_PATH = "/api/push/register";

let _initialised = false;
let _lastPostedToken = null;

/**
 * One-shot init.  Idempotent: safe to call twice (e.g. after login).
 *
 * `getUserId` is a function that returns the current user_id at
 * post-time — passed as a function (not a value) because login can
 * happen after init and we need the freshest id when the OS finally
 * fires `registration`.
 */
export async function initPushNotifications({ getUserId, navigate } = {}) {
  if (_initialised) return;
  if (typeof window === "undefined") return;
  if (!window.Capacitor || !window.Capacitor.isNativePlatform?.()) {
    // Web — push won't work, but we don't want to throw.
    return;
  }
  _initialised = true;

  let PushNotifications;
  try {
    ({ PushNotifications } = await import("@capacitor/push-notifications"));
  } catch (e) {
    console.warn("[push] plugin unavailable:", e);
    return;
  }

  // 1) Permissions
  try {
    let status = await PushNotifications.checkPermissions();
    if (status.receive !== "granted") {
      status = await PushNotifications.requestPermissions();
    }
    if (status.receive !== "granted") {
      console.info("[push] permission not granted");
      return;
    }
  } catch (e) {
    console.warn("[push] permission check failed:", e);
    return;
  }

  // 2) Register listeners BEFORE calling register() so the first
  // `registration` event isn't dropped.
  PushNotifications.addListener("registration", async (token) => {
    const tokenValue = token?.value;
    if (!tokenValue || tokenValue === _lastPostedToken) return;
    const userId = (typeof getUserId === "function" && getUserId()) || null;
    if (!userId) {
      // Not logged in yet — stash the token, retry after login.
      _lastPostedToken = tokenValue;
      try {
        window.localStorage.setItem("__pending_push_token", tokenValue);
      } catch {
        /* private mode */
      }
      return;
    }
    try {
      const platform = window.Capacitor.getPlatform?.() || "unknown";
      const apiUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      await fetch(`${apiUrl}${REGISTER_PATH}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: tokenValue,
          platform,
          device_label: navigator.userAgent || "",
          app_version: process.env.REACT_APP_VERSION || "",
        }),
      });
      _lastPostedToken = tokenValue;
      try { window.localStorage.removeItem("__pending_push_token"); } catch { /* ignore */ }
    } catch (e) {
      console.warn("[push] backend registration POST failed:", e);
    }
  });

  PushNotifications.addListener("registrationError", (error) => {
    console.warn("[push] registrationError:", error);
  });

  // Foreground notification — show a toast so the user sees something.
  // The OS won't draw a banner when the app is foregrounded by default.
  PushNotifications.addListener("pushNotificationReceived", (notification) => {
    const title = notification?.title || "ServiceOps";
    const body = notification?.body || "";
    try {
      toast(title, { description: body });
    } catch {
      // Sonner not mounted? Fall back to console — better than nothing.
      console.info(`[push fg] ${title}: ${body}`);
    }
  });

  // Background tap — navigate the user into the relevant screen.
  PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    const data = action?.notification?.data || {};
    const deepLink = data.deep_link || data.deepLink;
    if (deepLink && typeof navigate === "function") {
      try { navigate(deepLink); } catch { /* ignore */ }
    } else if (deepLink && typeof window !== "undefined") {
      // Hard-navigate fallback when react-router's navigate isn't wired.
      window.location.href = deepLink;
    }
  });

  // 3) Kick off the OS registration.
  try {
    await PushNotifications.register();
  } catch (e) {
    console.warn("[push] register() failed:", e);
  }
}


/**
 * Call after a successful login to flush the pre-login token to the
 * backend (it was stashed in localStorage during init when no user
 * was authenticated yet).
 */
export async function flushPendingPushToken(userId) {
  if (typeof window === "undefined") return;
  if (!userId) return;
  let stashed = null;
  try { stashed = window.localStorage.getItem("__pending_push_token"); } catch { /* ignore */ }
  if (!stashed) return;
  try {
    const apiUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    await fetch(`${apiUrl}${REGISTER_PATH}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: stashed,
        platform: window.Capacitor?.getPlatform?.() || "unknown",
      }),
    });
    _lastPostedToken = stashed;
    window.localStorage.removeItem("__pending_push_token");
  } catch (e) {
    console.warn("[push] flushPendingPushToken failed:", e);
  }
}
